/**
 * Клиент API (T11.2, `05_API.md` §2-3).
 *
 * ## Истечение токена не должно быть видно
 *
 * Токен доступа живёт пятнадцать минут, а человек листает объявления
 * дольше. Показать ему «войдите заново» посреди списка значит наказать
 * за то, что он засмотрелся. Поэтому 401 наружу не всплывает: клиент
 * меняет пару и повторяет запрос сам.
 *
 * ## Обновление идёт в одиночку
 *
 * Экран открывает несколько запросов разом, и все они упрутся в
 * просроченный токен одновременно. Обнови каждый сам по себе — первый
 * отзовёт токен, которым второй как раз воспользовался, и сервер сочтёт
 * это кражей: повторное использование отозванного отзывает все
 * (`modules/auth/sessions.ts`). Человека выбросило бы из приложения
 * ровно за то, что он открыл экран со списком.
 *
 * Отсюда единственное обновление на всех: остальные ждут его, а не
 * начинают своё.
 *
 * ## Токены живут в localStorage
 *
 * Не в памяти: приложение сворачивают и открывают заново десятки раз в
 * день, и вход при каждом открытии — лишняя секунда там, где обещана
 * скорость. И не в `CloudStorage` Telegram: он общий на все устройства,
 * а токен обновления привязан к одному.
 */

import { initData } from '../telegram/webapp';

const STORAGE_KEY = 'xoreva.tokens';

/** Пара токенов в том виде, в каком её отдаёт сервер. */
export interface Tokens {
  readonly access_token: string;
  readonly expires_at: string;
  readonly refresh_token: string;
  readonly refresh_expires_at: string;
}

/**
 * Отказ API.
 *
 * Код приходит от сервера и предназначен коду, а не человеку: по нему
 * экран решает, показать ли повтор, пустое состояние или переход к
 * оплате (`09_UI_UX.md` §6).
 */
export class ApiFailure extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

/** Конверт ответа. Поля есть всегда, неприменимые равны `null`. */
interface Envelope<T> {
  readonly success: boolean;
  readonly data: T | null;
  readonly meta: unknown;
  readonly error: { code: string; message: string; details?: unknown } | null;
}

export function loadTokens(): Tokens | null {
  const raw = localStorage.getItem(STORAGE_KEY);

  if (raw === null) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<Tokens>;

    return typeof parsed.access_token === 'string' && typeof parsed.refresh_token === 'string'
      ? (parsed as Tokens)
      : null;
  } catch {
    // Испорченное хранилище — не отказ, а повод войти заново
    return null;
  }
}

export function saveTokens(tokens: Tokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

export function forgetTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
}

async function open<T>(answer: Response): Promise<Envelope<T>> {
  let envelope: Envelope<T>;

  // Удаление отвечает 204 и телом не отвечает вовсе (`05_API.md` §9).
  // Разбирать пустоту как конверт значит получить отказ разбора на
  // успешном удалении — и показать «сервис не ответил» там, где он
  // ответил именно то, о чём просили
  if (answer.status === 204) {
    return { success: true, data: null, meta: null, error: null };
  }

  try {
    envelope = (await answer.json()) as Envelope<T>;
  } catch {
    // Ответ не конверт. Такое приходит от прокси и страниц ошибок, и
    // разбирать это как ответ API нельзя
    throw new ApiFailure('INTERNAL_ERROR', 'Ответ сервера не разобран', answer.status);
  }

  if (!answer.ok || envelope.error !== null) {
    const error = envelope.error;

    throw new ApiFailure(
      error?.code ?? 'INTERNAL_ERROR',
      error?.message ?? 'Запрос не выполнен',
      answer.status,
      error?.details,
    );
  }

  return envelope;
}

async function unwrap<T>(answer: Response): Promise<T> {
  return (await open<T>(answer)).data as T;
}

/** Обращение без токена: вход и обновление. */
async function post<T>(path: string, body: unknown): Promise<T> {
  const answer = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  return unwrap<T>(answer);
}

/**
 * Вход — тоже единственный на всех.
 *
 * Экран открывает несколько запросов разом, и при первом запуске
 * токенов нет ни у одного. Позволь каждому входить самостоятельно — все
 * пойдут с одной и той же строкой `initData`, первый её израсходует, а
 * остальные получат отказ защиты от повтора (`08_SECURITY.md` §2.1,
 * шаг 6). Строка подписана и остаётся действительной, поэтому без этой
 * защиты перехваченная строка годилась бы для входа весь свой срок; но
 * и обходить её повторными попытками нельзя — она права.
 *
 * Ошибка была ровно такой: «Радар» и три запроса справочников входили
 * вчетвером, побеждал один, а человек видел «Строка входа не принята» на
 * обоих экранах.
 */
let entering: Promise<Tokens> | null = null;

export function login(): Promise<Tokens> {
  entering ??= enter().finally(() => {
    entering = null;
  });

  return entering;
}

async function enter(): Promise<Tokens> {
  const raw = initData();

  if (raw === '') {
    throw new ApiFailure('UNAUTHORIZED', 'Приложение открыто не из Telegram', 401);
  }

  const tokens = await post<Tokens>('/v1/auth/telegram', { init_data: raw });
  saveTokens(tokens);

  return tokens;
}

/**
 * Обновление пары — единственное на всех.
 *
 * Обещание хранится в переменной модуля намеренно: это и есть «одно
 * обновление на приложение». Второй вызов во время первого получает то
 * же обещание, а не начинает своё.
 */
let refreshing: Promise<Tokens> | null = null;

function refresh(used: Tokens): Promise<Tokens> {
  const current = loadTokens();

  // Пару уже обновил другой запрос, пока мы ждали ответа на свой. Пойти
  // со своей устаревшей копией значит предъявить серверу отозванный
  // токен обновления, а он считает это кражей и отзывает разом все
  // (`modules/auth/sessions.ts`). Человека выбросило бы из приложения
  // за то, что два его экрана обновились одновременно
  if (current !== null && current.refresh_token !== used.refresh_token) {
    return Promise.resolve(current);
  }

  refreshing ??= post<Tokens>('/v1/auth/refresh', { refresh_token: used.refresh_token })
    .then((fresh) => {
      saveTokens(fresh);

      return fresh;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

/**
 * Токены для запроса: из хранилища либо новым входом.
 *
 * Вход здесь, а не отдельным действием при запуске: экран, открытый
 * первым, всё равно упрётся в отсутствие токенов, и заводить ради этого
 * особый порядок старта значит добавить состояние «ещё не вошли»,
 * которое пришлось бы учитывать каждому экрану.
 */
function ensure(): Promise<Tokens> {
  const stored = loadTokens();

  return stored === null ? login() : Promise.resolve(stored);
}

/** Страница выдачи: сами записи и то, чем продолжить. */
export interface PageOf<T> {
  readonly items: T[];
  readonly cursor: string | null;
  readonly hasMore: boolean;
}

export interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /**
   * Дополнительные заголовки запроса.
   *
   * Нужны одному пути — созданию платежа: `Idempotency-Key` обязателен
   * (`05_API.md` §11), и без него повторное нажатие «Оплатить»
   * выставило бы второй счёт. Заголовок токена ими не подменяется:
   * подстановка идёт после, а не до.
   */
  readonly headers?: Readonly<Record<string, string>>;
}

function send(path: string, options: RequestOptions, tokens: Tokens): Promise<Response> {
  const headers: Record<string, string> = {
    ...options.headers,
    // Токен ставится последним намеренно: чужой заголовок не должен
    // уметь его подменить
    authorization: `Bearer ${tokens.access_token}`,
  };

  if (options.body !== undefined) headers['content-type'] = 'application/json';

  return fetch(path, {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
}

/**
 * Запрос к API с подстановкой токена и незаметным обновлением.
 *
 * Повтор ровно один: если и после обновления пришёл 401, дело не в
 * сроке. Повторять дальше значило бы крутиться в петле, объясняя
 * человеку молчанием, что что-то не так.
 */
export async function requestPage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<PageOf<T>> {
  const envelope = await open<T[]>(await deliver(path, options));
  const meta = (envelope.meta ?? {}) as { cursor?: string | null; has_more?: boolean };

  return {
    items: envelope.data ?? [],
    cursor: meta.cursor ?? null,
    hasMore: meta.has_more === true,
  };
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return unwrap<T>(await deliver(path, options));
}

/**
 * Запрос с токеном и незаметным обновлением.
 *
 * Отдаёт ответ, а не разобранное тело: постраничной выдаче нужен `meta`,
 * и разбирать его дважды в двух местах значило бы получить два
 * представления одного конверта.
 */
async function deliver(path: string, options: RequestOptions): Promise<Response> {
  let tokens = await ensure();
  const answer = await send(path, options, tokens);

  if (answer.status !== 401) return answer;

  try {
    tokens = await refresh(tokens);
  } catch {
    // Токен обновления тоже не годится: просрочен, отозван или
    // хранилище досталось от прошлой учётной записи. Вход заново — не
    // ошибка, а обычный конец месяца
    forgetTokens();
    tokens = await login();
  }

  return send(path, options, tokens);
}
